import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Landmark,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  TrendingDown,
  Scale,
  Wallet,
  AlertCircle,
  Loader2,
  History,
  Info,
  Car,
  CreditCard,
  ArrowLeftRight,
  FileText,
  Percent,
  Globe,
  Banknote,
  CalendarDays,
  RefreshCw,
  Hourglass,
  CalendarClock,
} from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import { TABLA_ANIOS, ColsAnios, COL_ANIO_MONO } from "@/components/ui/tabla-anios";
import TablaDrivers from "./TablaDrivers";
import {
  getProyeccionOperaciones,
  getProyeccionResultado,
  crearProyeccionOperacion,
  actualizarProyeccionOperacion,
  eliminarProyeccionOperacion,
} from "@/lib/api";

// ---------------------------------------------------------------------------
// Formatos
// ---------------------------------------------------------------------------

// Formato único de proyección: una sola fuente en lib/utils (formatEur).
const formatCurrency = formatEur;

const nfPct = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// ---------------------------------------------------------------------------
// Cálculo client-side del cuadro de amortización (solo PREVIEW del dialog;
// la verdad la devuelve el backend al guardar). Misma matemática que el
// backend: cálculo mensual agregado por año.
// ---------------------------------------------------------------------------

// P17 (Pau oct-2026): meses de cuota de un préstamo del Pool desde el 1 de
// enero del primer año proyectado hasta el mes de vencimiento, ambos incluidos
// (espejo de `plazo_meses_existente` del backend). Sin mes en la fecha o con
// una fecha de fin anterior al primer año proyectado: años enteros.
export function mesesPool(fechaFin, primerAnio, aniosRestantes) {
  const s = String(fechaFin || "");
  const anioFin = parseInt(s.slice(0, 4), 10);
  const mesFin = parseInt(s.slice(5, 7), 10);
  if (!Number.isFinite(anioFin) || !(mesFin >= 1 && mesFin <= 12) || primerAnio == null || anioFin < primerAnio) {
    return Number(aniosRestantes) * 12;
  }
  return Math.max(1, Math.min(30 * 12, (anioFin - primerAnio) * 12 + mesFin));
}

export function calcularCuadroPrestamo({
  importe,
  anio_inicio,
  mes_inicio,
  plazo_anios,
  plazo_meses, // P17: plazo exacto en meses (Pool); manda sobre plazo_anios
  tipo_interes_pct,
  metodo,
  meses_carencia,
  carencia_intereses, // true = carencia TOTAL (capitaliza intereses)
}) {
  const P = Number(importe);
  const n = Number(plazo_meses) > 0 ? Math.round(Number(plazo_meses)) : Math.round(Number(plazo_anios) * 12);
  if (!P || P <= 0 || !n || n <= 0) return [];

  const i = Number(tipo_interes_pct || 0) / 12 / 100; // interés mensual
  const mesInicio = Math.min(12, Math.max(1, Number(mes_inicio) || 1)); // 1-12
  const carencia = Math.max(0, Math.min(n - 1, Math.round(Number(meses_carencia) || 0)));
  const carenciaTotal = Boolean(carencia_intereses);
  const nAmort = n - carencia; // meses que amortizan principal

  // Cálculo mes a mes (igual que el backend, dos fases) agregando por año natural.
  let saldo = P;
  const porAnio = new Map(); // anio → {cuota, intereses, principal, saldo_final, ...}
  const principalPagadoPorAnio = new Map();
  const slot = (m) => Number(anio_inicio) + Math.floor((mesInicio - 1 + m) / 12);
  const acumula = (anio, interesPagado, interesCap, principalMes) => {
    if (!porAnio.has(anio)) {
      porAnio.set(anio, {
        anio, cuota: 0, intereses: 0, principal: 0, saldo_final: 0,
        intereses_capitalizados: 0,
      });
    }
    const fila = porAnio.get(anio);
    fila.cuota += interesPagado + principalMes; // caja: lo capitalizado no se paga
    fila.intereses += interesPagado + interesCap; // devengado (va al PyG)
    fila.intereses_capitalizados += interesCap;
    fila.principal += principalMes;
    fila.saldo_final = Math.max(0, saldo);
    principalPagadoPorAnio.set(anio, (principalPagadoPorAnio.get(anio) || 0) + principalMes);
  };

  // Fase 1: carencia. Total → capitaliza (deuda crece); de capital → paga intereses.
  for (let m = 0; m < carencia; m++) {
    const anio = slot(m);
    const interesMes = saldo * i;
    if (carenciaTotal) {
      saldo += interesMes;
      acumula(anio, 0, interesMes, 0);
    } else {
      acumula(anio, interesMes, 0, 0);
    }
  }

  // Cuota recalculada sobre el saldo TRAS la carencia (capitalizado o no).
  const saldoPost = saldo;
  const cuotaFrances = i > 0 ? (saldoPost * i) / (1 - Math.pow(1 + i, -nAmort)) : saldoPost / nAmort;
  const principalLineal = saldoPost / nAmort;

  // Fase 2: amortización.
  for (let k = 0; k < nAmort; k++) {
    const m = carencia + k;
    const anio = slot(m);
    const interesMes = saldo * i;
    let principalMes = metodo === "lineal" ? principalLineal : cuotaFrances - interesMes;
    if (k === nAmort - 1) principalMes = saldo; // residuo de redondeo
    principalMes = Math.max(0, Math.min(principalMes, saldo));
    saldo -= principalMes;
    acumula(anio, interesMes, 0, principalMes);
  }

  const filas = Array.from(porAnio.values()).sort((a, b) => a.anio - b.anio);

  // Saldo CP = principal que vence el año natural siguiente; LP = resto
  filas.forEach((f) => {
    const venceSiguiente = principalPagadoPorAnio.get(f.anio + 1) || 0;
    f.saldo_cp = Math.min(venceSiguiente, f.saldo_final);
    f.saldo_lp = f.saldo_final - f.saldo_cp;
  });

  return filas;
}

// ---------------------------------------------------------------------------
// Tabla del cuadro de amortización (compartida: card y preview del dialog).
// Los años dentro del horizonte de proyección se tintan con bg-primary/5.
// ---------------------------------------------------------------------------

function TablaCuadroAmortizacion({ cuadro, aniosProyectados = [], compacta = false }) {
  const enHorizonte = useMemo(
    () => new Set((aniosProyectados || []).map(Number)),
    [aniosProyectados]
  );

  if (!cuadro || cuadro.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        Sin cuadro de amortización
      </p>
    );
  }
  const total = cuadro.reduce(
    (acc, f) => ({
      cuota: acc.cuota + (f.cuota || 0),
      intereses: acc.intereses + (f.intereses || 0),
      principal: acc.principal + (f.principal || 0),
    }),
    { cuota: 0, intereses: 0, principal: 0 }
  );

  const celda = (anio) =>
    cn(enHorizonte.has(Number(anio)) && "bg-primary/5");

  // Saldo inicial por fila (maqueta): saldo_final + principal − intereses
  // capitalizados de la propia fila. Es identidad del cuadro, no un recálculo.
  const inicialDe = (f) =>
    (f.saldo_final || 0) + (f.principal || 0) - (f.intereses_capitalizados || 0);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className={cn("w-[80px]", compacta && "h-8 text-xs")}>Año</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Saldo inicial</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Amortización</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Intereses</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Cuota</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Saldo final</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cuadro.map((fila) => (
            <TableRow key={fila.anio} className="hover:bg-muted/20">
              <TableCell className={cn("font-medium", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {fila.anio}
              </TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {formatCurrency(inicialDe(fila))}
              </TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {formatCurrency(fila.principal)}
              </TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {formatCurrency(fila.intereses)}
              </TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {formatCurrency(fila.cuota)}
              </TableCell>
              <TableCell className={cn("text-right", compacta && "py-1.5", celda(fila.anio))}>
                <div className={cn("font-mono tabular-nums text-sm", compacta && "text-xs")}>
                  {formatCurrency(fila.saldo_final)}
                </div>
                <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                  CP {formatCurrency(fila.saldo_cp)} · LP {formatCurrency(fila.saldo_lp)}
                </div>
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="border-t-2 font-semibold bg-primary/5 hover:bg-primary/10">
            <TableCell className={cn(compacta && "py-1.5 text-xs")}>Total</TableCell>
            <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs")}>
              {cuadro.length ? formatCurrency(inicialDe(cuadro[0])) : "—"}
            </TableCell>
            <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs")}>
              {formatCurrency(total.principal)}
            </TableCell>
            <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs")}>
              {formatCurrency(total.intereses)}
            </TableCell>
            <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs")}>
              {formatCurrency(total.cuota)}
            </TableCell>
            <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs")}>
              {cuadro.length ? formatCurrency(cuadro[cuadro.length - 1].saldo_final) : "—"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog crear/editar préstamo
// ---------------------------------------------------------------------------

const FORM_VACIO = {
  nombre: "",
  importe: "",
  anio_inicio: "",
  mes_inicio: "1", // R-9b: mes de constitución (default enero)
  plazo_anios: "",
  tipo_interes_pct: "",
  metodo: "frances",
  meses_carencia: "0", // R-9a: meses de carencia
  carencia_intereses: false, // false = carencia de capital (paga intereses); true = total
  // préstamo existente
  saldo_vivo: "",
  anios_restantes: "",
  // Punto 7 Héctor (oct-2026): el Pool se declara con fechas; los años
  // restantes se derivan de la fecha de fin (el año de fin cuenta entero).
  fecha_inicio: "",
  fecha_fin: "",
  // E10 (Pau oct-2026): cancelación anticipada del préstamo del Pool.
  cancelar: false,
  cancelacion_fecha: "",
  cancelacion_importe: "", // vacío = cancelación TOTAL
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function PrestamoDialog({ open, onOpenChange, prestamo, tipoOperacion = "prestamo", aniosProyectados, onGuardar, guardando }) {
  const [form, setForm] = useState(FORM_VACIO);

  // Variante del dialog: préstamo nuevo o préstamo ya existente en balance
  const esExistente = prestamo
    ? prestamo.tipo === "prestamo_existente"
    : tipoOperacion === "prestamo_existente";

  useEffect(() => {
    if (open) {
      if (prestamo) {
        const p = prestamo.params || {};
        setForm({
          nombre: prestamo.nombre || "",
          importe: p.importe != null ? String(p.importe) : "",
          anio_inicio: p.anio_inicio != null ? String(p.anio_inicio) : "",
          mes_inicio: p.mes_inicio != null ? String(p.mes_inicio) : "1",
          plazo_anios: p.plazo_anios != null ? String(p.plazo_anios) : "",
          tipo_interes_pct: p.tipo_interes_pct != null ? String(p.tipo_interes_pct) : "",
          metodo: p.metodo || "frances",
          meses_carencia: p.meses_carencia != null ? String(p.meses_carencia) : "0",
          carencia_intereses: !!p.carencia_intereses,
          saldo_vivo: p.saldo_vivo != null ? String(p.saldo_vivo) : "",
          anios_restantes: p.anios_restantes != null ? String(p.anios_restantes) : "",
          fecha_inicio: p.fecha_inicio || "",
          // Préstamo antiguo sin fechas: se precarga el fin equivalente a sus
          // años restantes para que el formulario nuevo salga relleno.
          fecha_fin: p.fecha_fin ||
            (p.anios_restantes != null && aniosProyectados[0] != null
              ? `${aniosProyectados[0] + p.anios_restantes - 1}-12-31`
              : ""),
          cancelar: !!p.cancelacion,
          cancelacion_fecha: p.cancelacion?.fecha || "",
          cancelacion_importe: p.cancelacion?.importe != null ? String(p.cancelacion.importe) : "",
        });
      } else {
        setForm({
          ...FORM_VACIO,
          anio_inicio: aniosProyectados[0] != null ? String(aniosProyectados[0]) : "",
        });
      }
    }
  }, [open, prestamo, aniosProyectados]);

  const set = (campo) => (e) =>
    setForm((f) => ({ ...f, [campo]: typeof e === "string" ? e : e.target.value }));

  const params = useMemo(() => {
    if (esExistente) {
      // Años naturales con cuota derivados de la fecha de fin; el cuadro
      // (backend y preview) termina en el MES exacto de vencimiento (P17).
      // Las fechas viajan en params para mostrarse en el Pool.
      const primerProy = aniosProyectados?.[0];
      const anioFin = form.fecha_fin ? parseInt(String(form.fecha_fin).slice(0, 4), 10) : NaN;
      const aniosDerivados = Number.isFinite(anioFin) && primerProy != null
        ? Math.max(1, anioFin - primerProy + 1)
        : parseInt(form.anios_restantes);
      return {
        saldo_vivo: parseFloat(form.saldo_vivo),
        anios_restantes: aniosDerivados,
        ...(form.fecha_inicio ? { fecha_inicio: form.fecha_inicio } : {}),
        ...(form.fecha_fin ? { fecha_fin: form.fecha_fin } : {}),
        tipo_interes_pct: parseFloat(form.tipo_interes_pct) || 0,
        metodo: form.metodo,
        // E10: cancelación anticipada (importe vacío = total). null al
        // desmarcar, para limpiar una cancelación guardada.
        cancelacion: form.cancelar && form.cancelacion_fecha
          ? { fecha: form.cancelacion_fecha,
              importe: form.cancelacion_importe !== "" ? parseFloat(form.cancelacion_importe) : null }
          : null,
      };
    }
    return {
      importe: parseFloat(form.importe),
      anio_inicio: parseInt(form.anio_inicio),
      mes_inicio: parseInt(form.mes_inicio) || 1,
      plazo_anios: parseInt(form.plazo_anios),
      tipo_interes_pct: parseFloat(form.tipo_interes_pct) || 0,
      metodo: form.metodo,
      meses_carencia: parseInt(form.meses_carencia) || 0,
      carencia_intereses: !!form.carencia_intereses,
    };
  }, [form, esExistente, aniosProyectados]);

  const valido = esExistente
    ? form.nombre.trim() && params.saldo_vivo > 0 && params.anios_restantes > 0
    : form.nombre.trim() &&
      params.importe > 0 &&
      params.anio_inicio > 0 &&
      params.plazo_anios > 0 &&
      params.meses_carencia >= 0 &&
      params.meses_carencia < params.plazo_anios * 12;

  // El cuadro del préstamo existente amortiza el saldo vivo desde el primer año proyectado
  const preview = useMemo(() => {
    if (!valido) return [];
    if (esExistente) {
      return calcularCuadroPrestamo({
        importe: params.saldo_vivo,
        anio_inicio: aniosProyectados[0],
        plazo_anios: params.anios_restantes,
        plazo_meses: mesesPool(params.fecha_fin, aniosProyectados[0], params.anios_restantes),
        tipo_interes_pct: params.tipo_interes_pct,
        metodo: params.metodo,
      });
    }
    return calcularCuadroPrestamo(params);
  }, [valido, params, esExistente, aniosProyectados]);

  const handleGuardar = () => {
    onGuardar({
      tipo: esExistente ? "prestamo_existente" : "prestamo",
      nombre: form.nombre.trim(),
      params,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {prestamo
              ? esExistente
                ? "Editar préstamo existente"
                : "Editar préstamo"
              : esExistente
              ? "Préstamo existente"
              : "Nuevo préstamo"}
          </DialogTitle>
          <DialogDescription>
            {esExistente
              ? "Registra un préstamo que ya tienes en balance para proyectar su amortización e intereses."
              : "Define las condiciones del préstamo. El cuadro de amortización se calcula automáticamente: los intereses impactan en el PyG y la deuda en el balance."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {esExistente && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p>
                No genera entrada de caja: ya está en tu balance. Su cuadro amortiza la
                deuda actual y lleva sus intereses al PyG.
              </p>
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="prestamo-nombre">Nombre</Label>
            <Input
              id="prestamo-nombre"
              placeholder={esExistente ? "Ej.: Hipoteca nave" : "Ej.: Préstamo ICO maquinaria"}
              value={form.nombre}
              onChange={set("nombre")}
              data-testid="prestamo-nombre"
            />
          </div>

          {esExistente ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-saldo-vivo">Saldo vivo pendiente (€)</Label>
                <Input
                  id="prestamo-saldo-vivo"
                  type="number"
                  min="0"
                  step="1000"
                  placeholder="60000"
                  value={form.saldo_vivo}
                  onChange={set("saldo_vivo")}
                  data-testid="prestamo-saldo-vivo"
                />
              </div>
              {/* Punto 7 Héctor (oct-2026): fechas en vez de "años restantes". */}
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-fecha-inicio">Fecha de inicio</Label>
                <Input
                  id="prestamo-fecha-inicio"
                  type="date"
                  value={form.fecha_inicio}
                  onChange={set("fecha_inicio")}
                  data-testid="prestamo-fecha-inicio"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-fecha-fin">Fecha de finalización</Label>
                <Input
                  id="prestamo-fecha-fin"
                  type="date"
                  value={form.fecha_fin}
                  onChange={set("fecha_fin")}
                  data-testid="prestamo-fecha-fin"
                />
                {form.fecha_fin && params.anios_restantes > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {mesesPool(params.fecha_fin, aniosProyectados?.[0], params.anios_restantes)} cuotas
                    mensuales desde enero de {aniosProyectados?.[0]} hasta el mes de vencimiento.
                  </p>
                )}
              </div>
              {/* E10 (Pau oct-2026): cancelación anticipada con traza. */}
              <div className="grid gap-1.5 rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20 p-2.5">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input type="checkbox" className="h-3.5 w-3.5 accent-primary"
                    checked={form.cancelar}
                    onChange={(e) => setForm((f) => ({ ...f, cancelar: e.target.checked }))}
                    data-testid="prestamo-cancelar" />
                  Cancelación anticipada
                </label>
                {form.cancelar && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-1">
                        <Label className="text-xs">Fecha de cancelación</Label>
                        <Input type="date" value={form.cancelacion_fecha}
                          onChange={set("cancelacion_fecha")}
                          data-testid="prestamo-cancelacion-fecha" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">Importe (vacío = total)</Label>
                        <Input type="number" min="0" step="1000" placeholder="Total"
                          value={form.cancelacion_importe}
                          onChange={set("cancelacion_importe")}
                          data-testid="prestamo-cancelacion-importe" />
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Sale la caja por el importe cancelado en ese año; desde la fecha
                      deja de generar amortización e intereses (en la parcial, el
                      capital restante re-amortiza con el mismo método y cuota
                      recalculada). El préstamo NO se borra: queda en el Pool marcado
                      como cancelado.
                    </p>
                  </>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-interes">Tipo de interés (%)</Label>
                <Input
                  id="prestamo-interes"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="6"
                  value={form.tipo_interes_pct}
                  onChange={set("tipo_interes_pct")}
                  data-testid="prestamo-interes"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-importe">Importe (€)</Label>
                <Input
                  id="prestamo-importe"
                  type="number"
                  min="0"
                  step="1000"
                  placeholder="80000"
                  value={form.importe}
                  onChange={set("importe")}
                  data-testid="prestamo-importe"
                />
              </div>
              <div className="grid gap-1.5">
                <Label>Año de inicio</Label>
                <Select value={form.anio_inicio} onValueChange={set("anio_inicio")}>
                  <SelectTrigger data-testid="prestamo-anio-inicio">
                    <SelectValue placeholder="Selecciona año" />
                  </SelectTrigger>
                  <SelectContent>
                    {aniosProyectados.map((anio) => (
                      <SelectItem key={anio} value={String(anio)}>
                        {anio}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Mes de constitución</Label>
                <Select value={form.mes_inicio} onValueChange={set("mes_inicio")}>
                  <SelectTrigger data-testid="prestamo-mes-inicio">
                    <SelectValue placeholder="Mes" />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((nombre, idx) => (
                      <SelectItem key={idx + 1} value={String(idx + 1)}>
                        {nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-plazo">Plazo (años)</Label>
                <Input
                  id="prestamo-plazo"
                  type="number"
                  min="1"
                  max="40"
                  step="1"
                  placeholder="5"
                  value={form.plazo_anios}
                  onChange={set("plazo_anios")}
                  data-testid="prestamo-plazo"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-interes">Tipo de interés (%)</Label>
                <Input
                  id="prestamo-interes"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="6"
                  value={form.tipo_interes_pct}
                  onChange={set("tipo_interes_pct")}
                  data-testid="prestamo-interes"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="prestamo-carencia">Carencia (meses)</Label>
                <Input
                  id="prestamo-carencia"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={form.meses_carencia}
                  onChange={set("meses_carencia")}
                  data-testid="prestamo-carencia"
                />
                <p className="text-[11px] text-muted-foreground">
                  {form.carencia_intereses
                    ? "Carencia total: durante estos meses no se paga nada; los intereses se capitalizan (la deuda crece)."
                    : "Carencia de capital: durante estos meses solo se pagan intereses."}
                </p>
                {(parseInt(form.meses_carencia) || 0) > 0 && (
                  <label className="flex items-start gap-2 mt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5 rounded border-input accent-primary"
                      checked={!!form.carencia_intereses}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, carencia_intereses: e.target.checked }))
                      }
                      data-testid="prestamo-carencia-intereses"
                    />
                    <span className="text-[11px] text-muted-foreground leading-tight">
                      Carencia de intereses también (se capitalizan)
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="grid gap-1.5">
            <Label>Método de amortización</Label>
            <RadioGroup
              value={form.metodo}
              onValueChange={set("metodo")}
              className="flex flex-col sm:flex-row gap-2"
            >
              <label
                className={cn(
                  "flex items-start gap-2 rounded-lg border p-3 cursor-pointer flex-1 transition-colors",
                  form.metodo === "frances" ? "border-primary bg-primary/5" : "hover:bg-muted/30"
                )}
              >
                <RadioGroupItem value="frances" className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Francés</div>
                  <div className="text-xs text-muted-foreground">Cuota constante</div>
                </div>
              </label>
              <label
                className={cn(
                  "flex items-start gap-2 rounded-lg border p-3 cursor-pointer flex-1 transition-colors",
                  form.metodo === "lineal" ? "border-primary bg-primary/5" : "hover:bg-muted/30"
                )}
              >
                <RadioGroupItem value="lineal" className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Lineal</div>
                  <div className="text-xs text-muted-foreground">Amortización constante</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Preview del cuadro */}
          {preview.length > 0 && (
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Vista previa del cuadro
                </span>
                <Badge variant="outline" className="text-[11px]">
                  Orientativa — el cálculo definitivo se hace al guardar
                </Badge>
              </div>
              <TablaCuadroAmortizacion cuadro={preview} aniosProyectados={aniosProyectados} compacta />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!valido || guardando} data-testid="prestamo-guardar">
            {guardando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {prestamo ? "Guardar cambios" : "Crear préstamo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card de un préstamo
// ---------------------------------------------------------------------------

function PrestamoCard({ prestamo, aniosProyectados, readOnly, onEditar, onBorrar }) {
  const p = prestamo.params || {};
  const metodoLabel = p.metodo === "lineal" ? "Lineal" : "Francés";
  const esExistente = prestamo.tipo === "prestamo_existente";

  // Fichas de métricas (maqueta 2026-08-19): icono + etiqueta + valor.
  const metricas = esExistente
    ? [
        { icon: Banknote, label: "Saldo vivo", valor: formatCurrency(p.saldo_vivo) },
        // Punto 7 (oct-2026): con fechas declaradas se muestran; los años de
        // cuota derivados quedan como apoyo.
        p.fecha_fin
          ? { icon: CalendarDays, label: "Vencimiento",
              valor: `${p.fecha_inicio ? `${p.fecha_inicio} → ` : ""}${p.fecha_fin} (${p.anios_restantes} año${p.anios_restantes !== 1 ? "s" : ""})` }
          : { icon: CalendarDays, label: "Años restantes", valor: `${p.anios_restantes} año${p.anios_restantes !== 1 ? "s" : ""}` },
        { icon: Percent, label: "Tipo de interés", valor: `${nfPct.format(p.tipo_interes_pct ?? 0)} %` },
        { icon: RefreshCw, label: "Sistema", valor: metodoLabel },
        // E10: traza de la cancelación anticipada — el préstamo sigue visible.
        // P16: el importe de la ficha es el REAL del backend (`cancelado_real`,
        // saldo vivo en la fecha; en parciales, el importe efectivo si el
        // tecleado superaba el saldo). Sin fallback al saldo a 1 de enero.
        ...(p.cancelacion ? [(() => {
          const real = prestamo.cancelado_real
            ?? (prestamo.cuadro || []).reduce((t, f) => t + (f.cancelado || 0), 0);
          return {
            icon: Hourglass, label: "Cancelación anticipada",
            valor: `${p.cancelacion.importe != null ? "Parcial" : "Total"}${
              real > 0 ? ` (${formatCurrency(real)})` : ""} el ${p.cancelacion.fecha}`,
          };
        })()] : []),
      ]
    : [
        { icon: Banknote, label: "Importe", valor: formatCurrency(p.importe) },
        { icon: CalendarDays, label: "Plazo", valor: `${p.plazo_anios} año${p.plazo_anios !== 1 ? "s" : ""}` },
        { icon: Percent, label: "Tipo de interés", valor: `${nfPct.format(p.tipo_interes_pct ?? 0)} %` },
        { icon: RefreshCw, label: "Sistema", valor: metodoLabel },
        { icon: CalendarClock, label: "Inicio", valor: `${p.mes_inicio && p.mes_inicio !== 1 ? `${MESES[p.mes_inicio - 1]} ` : ""}${p.anio_inicio}` },
        { icon: Hourglass, label: "Carencia", valor: p.meses_carencia > 0 ? `${p.meses_carencia} meses${p.carencia_intereses ? " (total)" : ""}` : "—" },
      ];

  return (
    <Card data-testid={`prestamo-card-${prestamo.id}`}>
      <CardContent className="pt-4">
        {/* Editar / eliminar arriba a la derecha del panel (maqueta) */}
        {!readOnly && (
          <div className="flex justify-end gap-1 -mt-1 mb-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEditar(prestamo)}
              data-testid={`prestamo-editar-${prestamo.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onBorrar(prestamo)}
              data-testid={`prestamo-borrar-${prestamo.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 items-start">
          {/* Izquierda: nombre + fichas de métricas + chips de impacto */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2 min-w-0">
              {esExistente ? (
                <History className="h-4 w-4 text-primary shrink-0" />
              ) : (
                <Landmark className="h-4 w-4 text-primary shrink-0" />
              )}
              <h3 className="font-semibold truncate">{prestamo.nombre}</h3>
              {esExistente && (
                <Badge variant="secondary" className="text-[11px] shrink-0">
                  Existente
                </Badge>
              )}
            </div>
            <div className={cn("grid gap-px rounded-md border bg-border overflow-hidden",
                               esExistente ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3")}>
              {metricas.map(({ icon: Icon, label, valor }) => (
                <div key={label} className="bg-card p-3 text-center space-y-1">
                  <Icon className="h-4 w-4 mx-auto text-muted-foreground" />
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                  <div className="font-mono tabular-nums text-sm font-semibold">{valor}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="text-[11px] gap-1 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800">
                <TrendingDown className="h-3 w-3" />
                → PyG: intereses
              </Badge>
              <Badge variant="outline" className="text-[11px] gap-1 text-sky-700 dark:text-sky-400 border-sky-300 dark:border-sky-800">
                <Scale className="h-3 w-3" />
                → Balance: deuda CP/LP
              </Badge>
              {!esExistente && (
                <Badge variant="outline" className="text-[11px] gap-1 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800">
                  <Wallet className="h-3 w-3" />
                  → Caja: +{formatCurrency(p.importe)} en {p.anio_inicio}
                </Badge>
              )}
            </div>
          </div>
          {/* Derecha: cuadro de amortización SIEMPRE visible (maqueta) */}
          <div className="rounded-lg border">
            <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
              <h4 className="text-sm font-semibold">Cuadro de amortización</h4>
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <TablaCuadroAmortizacion
              cuadro={prestamo.cuadro}
              aniosProyectados={aniosProyectados}
              compacta
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Prefijos bloqueados en "Otros costes financieros" (la 662 se informa desde
// el Pool bancario). Constante de módulo congelada (R-1): un literal inline
// crearía un array nuevo por render e invalidaría los memos de TablaDrivers.
const BLOQUEO_662 = Object.freeze(["662"]);

// Estructura contable completa del grupo 66 (email del cliente 2026-08-19):
// estas 9 cuentas se muestran SIEMPRE en "Otros costes financieros", a guiones
// si no tienen datos. La 662 va bloqueada (sus intereses salen del Pool).
const CUENTAS_GRUPO_66 = [
  { codigo: "661", nombre: "Intereses de obligaciones y bonos" },
  { codigo: "662", nombre: "Intereses de deudas" },
  { codigo: "663", nombre: "Pérdidas por valoración de instrumentos financieros" },
  { codigo: "664", nombre: "Gastos por dividendos de acciones o participaciones consideradas pasivos financieros" },
  { codigo: "665", nombre: "Intereses por descuento de efectos y operaciones de factoring" },
  { codigo: "666", nombre: "Pérdidas en participaciones y valores representativos de deuda" },
  { codigo: "667", nombre: "Pérdidas de créditos no comerciales" },
  { codigo: "668", nombre: "Diferencias negativas de cambio" },
  { codigo: "669", nombre: "Otros gastos financieros" },
];

// ---------------------------------------------------------------------------
// Dialog crear/editar RENTING
// Gasto de explotación (baja EBITDA). params exactos que valida el backend:
//   { anio_inicio, cuota_anual, duracion_anios }
// ---------------------------------------------------------------------------

const RENTING_FORM_VACIO = {
  nombre: "",
  anio_inicio: "",
  cuota_anual: "",
  duracion_anios: "",
};

function RentingDialog({ open, onOpenChange, renting, aniosProyectados, onGuardar, guardando }) {
  const [form, setForm] = useState(RENTING_FORM_VACIO);

  useEffect(() => {
    if (open) {
      if (renting) {
        const p = renting.params || {};
        setForm({
          nombre: renting.nombre || "",
          anio_inicio: p.anio_inicio != null ? String(p.anio_inicio) : "",
          cuota_anual: p.cuota_anual != null ? String(p.cuota_anual) : "",
          duracion_anios: p.duracion_anios != null ? String(p.duracion_anios) : "",
        });
      } else {
        setForm({
          ...RENTING_FORM_VACIO,
          anio_inicio: aniosProyectados[0] != null ? String(aniosProyectados[0]) : "",
        });
      }
    }
  }, [open, renting, aniosProyectados]);

  const set = (campo) => (e) =>
    setForm((f) => ({ ...f, [campo]: typeof e === "string" ? e : e.target.value }));

  const params = useMemo(
    () => ({
      anio_inicio: parseInt(form.anio_inicio),
      cuota_anual: parseFloat(form.cuota_anual),
      duracion_anios: parseInt(form.duracion_anios),
    }),
    [form]
  );

  const aniosSet = useMemo(() => new Set(aniosProyectados.map(Number)), [aniosProyectados]);

  const valido =
    form.nombre.trim() &&
    aniosSet.has(params.anio_inicio) &&
    params.cuota_anual > 0 &&
    params.duracion_anios >= 1 &&
    params.duracion_anios <= 30;

  // Preview: años que soportan la cuota (los que caen en el horizonte se tintan).
  const preview = useMemo(() => {
    if (!valido) return [];
    return Array.from({ length: params.duracion_anios }, (_, k) => ({
      anio: params.anio_inicio + k,
      cuota: params.cuota_anual,
    }));
  }, [valido, params]);

  const enHorizonte = useMemo(
    () => new Set((aniosProyectados || []).map(Number)),
    [aniosProyectados]
  );

  const handleGuardar = () => {
    onGuardar({ tipo: "renting", nombre: form.nombre.trim(), params });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{renting ? "Editar renting" : "Nuevo renting"}</DialogTitle>
          <DialogDescription>
            Cuota anual de renting durante un número de años. Es un gasto de explotación:
            reduce el EBITDA cada año que dura el contrato.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="renting-nombre">Nombre</Label>
            <Input
              id="renting-nombre"
              placeholder="Ej.: Renting flota comercial"
              value={form.nombre}
              onChange={set("nombre")}
              data-testid="renting-nombre"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Año de inicio</Label>
              <Select value={form.anio_inicio} onValueChange={set("anio_inicio")}>
                <SelectTrigger data-testid="renting-anio-inicio">
                  <SelectValue placeholder="Selecciona año" />
                </SelectTrigger>
                <SelectContent>
                  {aniosProyectados.map((anio) => (
                    <SelectItem key={anio} value={String(anio)}>
                      {anio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="renting-duracion">Duración (años)</Label>
              <Input
                id="renting-duracion"
                type="number"
                min="1"
                max="30"
                step="1"
                placeholder="4"
                value={form.duracion_anios}
                onChange={set("duracion_anios")}
                data-testid="renting-duracion"
              />
            </div>
            <div className="grid gap-1.5 sm:col-span-2">
              <Label htmlFor="renting-cuota">Cuota anual (€)</Label>
              <Input
                id="renting-cuota"
                type="number"
                min="0"
                step="100"
                placeholder="12000"
                value={form.cuota_anual}
                onChange={set("cuota_anual")}
                data-testid="renting-cuota"
              />
            </div>
          </div>

          {preview.length > 0 && (
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Cuotas previstas
                </span>
                <Badge variant="outline" className="text-[11px]">
                  Total {formatCurrency(params.cuota_anual * params.duracion_anios)}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="h-8 text-xs w-[80px]">Año</TableHead>
                      <TableHead className="h-8 text-xs text-right">Cuota (gasto)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((f) => (
                      <TableRow key={f.anio} className="hover:bg-muted/20">
                        <TableCell
                          className={cn(
                            "py-1.5 text-xs font-medium",
                            enHorizonte.has(f.anio) && "bg-primary/5"
                          )}
                        >
                          {f.anio}
                          {!enHorizonte.has(f.anio) && (
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              (fuera de horizonte)
                            </span>
                          )}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "py-1.5 text-xs text-right font-mono tabular-nums",
                            enHorizonte.has(f.anio) && "bg-primary/5"
                          )}
                        >
                          {formatCurrency(f.cuota)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={!valido || guardando}
            data-testid="renting-guardar"
          >
            {guardando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {renting ? "Guardar cambios" : "Crear renting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Dialog crear/editar PÓLIZA DE CRÉDITO
// Línea revolving. params exactos que valida el backend:
//   { limite, tipo_interes_pct, dispuesto_por_anio: { "2025": saldo, ... } }
// El usuario indica el saldo DISPUESTO al cierre de cada año proyectado.
// ---------------------------------------------------------------------------

const POLIZA_FORM_VACIO = {
  nombre: "",
  limite: "",
  tipo_interes_pct: "",
  dispuesto: {}, // { [anio]: string }
};

function PolizaDialog({ open, onOpenChange, poliza, aniosProyectados, onGuardar, guardando }) {
  const [form, setForm] = useState(POLIZA_FORM_VACIO);

  useEffect(() => {
    if (open) {
      const disp = {};
      aniosProyectados.forEach((a) => {
        const v = poliza?.params?.dispuesto_por_anio?.[String(a)];
        disp[String(a)] = v != null ? String(v) : "";
      });
      if (poliza) {
        const p = poliza.params || {};
        setForm({
          nombre: poliza.nombre || "",
          limite: p.limite != null ? String(p.limite) : "",
          tipo_interes_pct: p.tipo_interes_pct != null ? String(p.tipo_interes_pct) : "",
          dispuesto: disp,
        });
      } else {
        setForm({ ...POLIZA_FORM_VACIO, dispuesto: disp });
      }
    }
  }, [open, poliza, aniosProyectados]);

  const set = (campo) => (e) =>
    setForm((f) => ({ ...f, [campo]: typeof e === "string" ? e : e.target.value }));

  const setDispuesto = (anio) => (e) =>
    setForm((f) => ({
      ...f,
      dispuesto: { ...f.dispuesto, [String(anio)]: e.target.value },
    }));

  const params = useMemo(() => {
    const dispuesto_por_anio = {};
    aniosProyectados.forEach((a) => {
      dispuesto_por_anio[String(a)] = parseFloat(form.dispuesto[String(a)]) || 0;
    });
    return {
      limite: parseFloat(form.limite),
      tipo_interes_pct: parseFloat(form.tipo_interes_pct) || 0,
      dispuesto_por_anio,
    };
  }, [form, aniosProyectados]);

  // Un saldo es inválido si es negativo o supera el límite.
  const saldoInvalido = (anio) => {
    const v = params.dispuesto_por_anio[String(anio)];
    return !(v >= 0 && (!(params.limite > 0) || v <= params.limite));
  };

  const saldosValidos =
    params.limite > 0 &&
    aniosProyectados.every((a) => {
      const v = params.dispuesto_por_anio[String(a)];
      return v >= 0 && v <= params.limite;
    });

  const valido =
    form.nombre.trim() &&
    params.limite > 0 &&
    params.tipo_interes_pct >= 0 &&
    saldosValidos;

  const handleGuardar = () => {
    onGuardar({ tipo: "poliza", nombre: form.nombre.trim(), params });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{poliza ? "Editar producto de circulante" : "Producto de circulante (póliza / factoring / confirming)"}</DialogTitle>
          <DialogDescription>
            Cualquier producto de circulante revolving (póliza, factoring, confirming…).
            Indica el saldo dispuesto al cierre de cada año: los intereses van al PyG y el
            saldo a deuda a corto plazo. Si no te renuevan un producto dispuesto, pon el
            dispuesto a 0 ese año: el modelo genera la devolución (salida de caja).
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="poliza-nombre">Nombre</Label>
            <Input
              id="poliza-nombre"
              placeholder="Ej.: Póliza de circulante"
              value={form.nombre}
              onChange={set("nombre")}
              data-testid="poliza-nombre"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="poliza-limite">Límite (€)</Label>
              <Input
                id="poliza-limite"
                type="number"
                min="0"
                step="1000"
                placeholder="100000"
                value={form.limite}
                onChange={set("limite")}
                data-testid="poliza-limite"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="poliza-interes">Tipo de interés (%)</Label>
              <Input
                id="poliza-interes"
                type="number"
                min="0"
                step="0.1"
                placeholder="5"
                value={form.tipo_interes_pct}
                onChange={set("tipo_interes_pct")}
                data-testid="poliza-interes"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Saldo dispuesto al cierre de cada año (€)</Label>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="h-9 text-xs w-[90px]">Año</TableHead>
                    <TableHead className="h-9 text-xs">Dispuesto</TableHead>
                    <TableHead className="h-9 text-xs text-right w-[110px]">
                      Interés estim.
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aniosProyectados.map((anio) => {
                    const dispuesto = params.dispuesto_por_anio[String(anio)];
                    const invalido = saldoInvalido(anio);
                    return (
                      <TableRow key={anio} className="hover:bg-muted/20">
                        <TableCell className="py-1.5 text-xs font-medium">{anio}</TableCell>
                        <TableCell className="py-1.5">
                          <Input
                            type="number"
                            min="0"
                            step="1000"
                            placeholder="0"
                            value={form.dispuesto[String(anio)] ?? ""}
                            onChange={setDispuesto(anio)}
                            className={cn(
                              "h-8 text-xs",
                              invalido && "border-destructive focus-visible:ring-destructive"
                            )}
                            data-testid={`poliza-dispuesto-${anio}`}
                          />
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums text-muted-foreground">
                          {formatCurrency((dispuesto * params.tipo_interes_pct) / 100)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {params.limite > 0 && !saldosValidos && (
              <p className="text-[11px] text-destructive">
                Cada saldo dispuesto debe estar entre 0 y el límite ({formatCurrency(params.limite)}).
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={!valido || guardando}
            data-testid="poliza-guardar"
          >
            {guardando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {poliza ? "Guardar cambios" : "Crear producto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Dialog crear/editar MOVIMIENTO DE BALANCE (evento)
// Apunte manual de partida doble en un año futuro concreto. params exactos que
// valida el backend:
//   { anio, apuntes: [ { destino, debe, haber } ] }
// Regla clave: Σdebe == Σhaber (el backend rechaza con 400 si no cuadra).
// Sirve para programar una ampliación de capital (Debe Tesorería / Haber
// Patrimonio neto), mover deuda o reclasificar partidas.
// ---------------------------------------------------------------------------

// E12 (revisión Pau oct-2026): los destinos son las LÍNEAS del balance
// proyectado (romanos PGC Pymes), agrupadas como en el balance, y cada una
// sale por su bloque del flujo de caja (mismo criterio que el EFE
// histórico). Las claves legacy se mantienen para las hipótesis guardadas.
// `nof: true` = admite la casilla "afecta a la NOF".
const DESTINOS_BALANCE = [
  { grupo: "Activo no corriente" },
  { value: "anc_i_intangible", label: "I. Inmovilizado intangible", flujo: "Inversión" },
  { value: "inmovilizado", label: "II. Inmovilizado material", flujo: "Inversión" },
  { value: "anc_iii_inv_inmobiliarias", label: "III. Inversiones inmobiliarias", flujo: "Inversión" },
  { value: "anc_iv_grupo_lp", label: "IV. Inversiones en empresas del grupo y asociadas a l/p", flujo: "Inversión" },
  { value: "anc_v_inv_fin_lp", label: "V. Inversiones financieras a l/p", flujo: "Inversión" },
  { value: "anc_vi_imp_diferido", label: "VI. Activos por impuesto diferido", flujo: "Inversión" },
  { grupo: "Activo corriente" },
  { value: "existencias", label: "I. Existencias", flujo: "Operativo", nof: true },
  { value: "clientes", label: "II. Deudores comerciales y otras cuentas a cobrar", flujo: "Operativo", nof: true },
  { value: "ac_iii_grupo_cp", label: "III. Inversiones en empresas del grupo y asociadas a c/p", flujo: "Inversión" },
  { value: "ac_iv_inv_fin_cp", label: "IV. Inversiones financieras a c/p", flujo: "Inversión" },
  { value: "ac_v_periodificaciones", label: "V. Periodificaciones a c/p (activo)", flujo: "Operativo" },
  { value: "tesoreria", label: "VI. Efectivo (la pata de caja)", flujo: "—" },
  { grupo: "Patrimonio neto" },
  { value: "pn_capital", label: "I. Capital", flujo: "Financiación" },
  { value: "pn_prima", label: "II. Prima de emisión", flujo: "Financiación" },
  { value: "pn_reservas", label: "III. Reservas", flujo: "Financiación" },
  { value: "pn_otras_aportaciones", label: "VI. Otras aportaciones de socios", flujo: "Financiación" },
  { value: "patrimonio_neto", label: "Patrimonio neto (genérico, legado)", flujo: "Financiación" },
  { grupo: "Pasivo no corriente" },
  { value: "pnc_i_provisiones", label: "I. Provisiones a l/p", flujo: "Operativo" },
  { value: "deuda_lp", label: "II. Deudas a l/p", flujo: "Financiación" },
  { value: "pnc_iii_grupo_lp", label: "III. Deudas con empresas del grupo l/p", flujo: "Financiación" },
  { value: "pnc_iv_imp_diferido", label: "IV. Pasivos por impuesto diferido", flujo: "Financiación" },
  { value: "pnc_v_periodificaciones", label: "V. Periodificaciones a l/p", flujo: "Operativo" },
  { grupo: "Pasivo corriente" },
  { value: "pc_i_provisiones", label: "I. Provisiones a c/p", flujo: "Operativo" },
  { value: "deuda_cp", label: "II. Deudas a c/p", flujo: "Financiación" },
  { value: "pc_iii_grupo_cp", label: "III. Deudas con empresas del grupo c/p", flujo: "Financiación" },
  { value: "proveedores", label: "IV. Acreedores comerciales y otras cuentas a pagar", flujo: "Operativo", nof: true },
  { value: "pc_v_periodificaciones", label: "V. Periodificaciones a c/p (pasivo)", flujo: "Operativo" },
  { grupo: "Legado (hipótesis antiguas)" },
  { value: "otros_activos", label: "Otros activos (genérico, legado)", flujo: "Inversión" },
  { value: "otros_pasivos", label: "Otros pasivos (genérico, legado)", flujo: "Financiación" },
];

const DESTINO_LABEL = Object.fromEntries(
  DESTINOS_BALANCE.filter((d) => d.value).map((d) => [d.value, d.label])
);
const DESTINO_NOF = new Set(
  DESTINOS_BALANCE.filter((d) => d.value && d.nof).map((d) => d.value)
);

const eventoFilaVacia = () => ({ destino: "", debe: "", haber: "", afecta_nof: true });

export function EventoDialog({ open, onOpenChange, evento, aniosProyectados, onGuardar, guardando }) {
  const [nombre, setNombre] = useState("");
  const [anio, setAnio] = useState("");
  const [filas, setFilas] = useState([]);

  useEffect(() => {
    if (open) {
      if (evento) {
        const p = evento.params || {};
        const aps = Array.isArray(p.apuntes) ? p.apuntes : [];
        setNombre(evento.nombre || "");
        setAnio(p.anio != null ? String(p.anio) : "");
        setFilas(
          aps.length > 0
            ? aps.map((a) => ({
                destino: a.destino || "",
                debe: a.debe ? String(a.debe) : "",
                haber: a.haber ? String(a.haber) : "",
                afecta_nof: a.afecta_nof !== false,
              }))
            : [eventoFilaVacia(), eventoFilaVacia()]
        );
      } else {
        setNombre("");
        setAnio(aniosProyectados[0] != null ? String(aniosProyectados[0]) : "");
        setFilas([eventoFilaVacia(), eventoFilaVacia()]);
      }
    }
  }, [open, evento, aniosProyectados]);

  const setFila = (idx, campo, valor) =>
    setFilas((fs) => fs.map((f, i) => (i === idx ? { ...f, [campo]: valor } : f)));
  const addFila = () => setFilas((fs) => [...fs, eventoFilaVacia()]);
  const removeFila = (idx) =>
    setFilas((fs) => (fs.length <= 1 ? fs : fs.filter((_, i) => i !== idx)));

  const totales = useMemo(() => {
    let debe = 0;
    let haber = 0;
    filas.forEach((f) => {
      debe += parseFloat(f.debe) || 0;
      haber += parseFloat(f.haber) || 0;
    });
    return { debe, haber, diferencia: debe - haber };
  }, [filas]);

  const cuadra = Math.abs(totales.diferencia) <= 0.01;
  const hayImporte = totales.debe > 0 || totales.haber > 0;
  const importesValidos = useMemo(
    () =>
      filas.every(
        (f) => (parseFloat(f.debe) || 0) >= 0 && (parseFloat(f.haber) || 0) >= 0
      ),
    [filas]
  );

  const aniosSet = useMemo(() => new Set(aniosProyectados.map(Number)), [aniosProyectados]);

  const params = useMemo(() => {
    const apuntes = filas
      .filter(
        (f) =>
          f.destino &&
          ((parseFloat(f.debe) || 0) > 0 || (parseFloat(f.haber) || 0) > 0)
      )
      .map((f) => ({
        destino: f.destino,
        debe: parseFloat(f.debe) || 0,
        haber: parseFloat(f.haber) || 0,
        // E2: solo viaja cuando el usuario lo desmarca en una línea que lo admite.
        ...(DESTINO_NOF.has(f.destino) && f.afecta_nof === false
          ? { afecta_nof: false } : {}),
      }));
    return { anio: parseInt(anio), apuntes };
  }, [filas, anio]);

  const valido =
    nombre.trim() &&
    aniosSet.has(params.anio) &&
    params.apuntes.length >= 1 &&
    hayImporte &&
    importesValidos &&
    cuadra;

  const handleGuardar = () => {
    onGuardar({ tipo: "evento_balance", nombre: nombre.trim(), params });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {evento ? "Editar movimiento de balance" : "Movimiento de balance (evento)"}
          </DialogTitle>
          <DialogDescription>
            Programa en un año futuro un apunte manual de partida doble (p. ej. una
            ampliación de capital: Debe Tesorería / Haber Patrimonio neto). El sistema no te
            deja guardar si el debe y el haber no cuadran.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="evento-nombre">Concepto</Label>
              <Input
                id="evento-nombre"
                placeholder="Ej.: Ampliación de capital"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                data-testid="evento-nombre"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Año</Label>
              <Select value={anio} onValueChange={setAnio}>
                <SelectTrigger data-testid="evento-anio">
                  <SelectValue placeholder="Selecciona año" />
                </SelectTrigger>
                <SelectContent>
                  {aniosProyectados.map((a) => (
                    <SelectItem key={a} value={String(a)}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Apuntes (partida doble)</Label>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="h-9 text-xs">Concepto</TableHead>
                    <TableHead className="h-9 text-xs text-right w-[130px]">Debe (€)</TableHead>
                    <TableHead className="h-9 text-xs text-right w-[130px]">Haber (€)</TableHead>
                    <TableHead className="h-9 w-[44px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas.map((fila, idx) => (
                    <TableRow key={idx} className="hover:bg-muted/20">
                      <TableCell className="py-1.5">
                        <Select
                          value={fila.destino}
                          onValueChange={(v) => setFila(idx, "destino", v)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`evento-destino-${idx}`}>
                            <SelectValue placeholder="Selecciona concepto" />
                          </SelectTrigger>
                          <SelectContent>
                            {DESTINOS_BALANCE.map((d, i) =>
                              d.grupo ? (
                                <div key={`g${i}`} className="px-2 py-1 text-[10px] font-semibold uppercase text-muted-foreground">
                                  {d.grupo}
                                </div>
                              ) : (
                                <SelectItem key={d.value} value={d.value}>
                                  {d.label}
                                  <span className="ml-1 text-[10px] text-muted-foreground">· {d.flujo}</span>
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        {DESTINO_NOF.has(fila.destino) && (
                          <label className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                            <input type="checkbox" className="h-3 w-3 accent-primary"
                              checked={fila.afecta_nof !== false}
                              onChange={(e) => setFila(idx, "afecta_nof", e.target.checked)}
                              data-testid={`evento-nof-${idx}`} />
                            Afecta a la NOF (desmarca si es un saldo excluido: intercompany, AIE…)
                          </label>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <Input
                          type="number"
                          min="0"
                          step="1000"
                          placeholder="0"
                          value={fila.debe}
                          onChange={(e) => setFila(idx, "debe", e.target.value)}
                          className="h-8 text-xs text-right font-mono tabular-nums"
                          data-testid={`evento-debe-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="py-1.5">
                        <Input
                          type="number"
                          min="0"
                          step="1000"
                          placeholder="0"
                          value={fila.haber}
                          onChange={(e) => setFila(idx, "haber", e.target.value)}
                          className="h-8 text-xs text-right font-mono tabular-nums"
                          data-testid={`evento-haber-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="py-1.5 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => removeFila(idx)}
                          disabled={filas.length <= 1}
                          data-testid={`evento-quitar-${idx}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={addFila}
              data-testid="evento-anadir-fila"
            >
              <Plus className="h-4 w-4 mr-1" />
              Añadir apunte
            </Button>
          </div>

          {/* Indicador en vivo del cuadre */}
          <div className="rounded-lg border bg-muted/20 p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span className="text-muted-foreground">
                  Total Debe:{" "}
                  <span className="font-mono tabular-nums font-medium text-foreground">
                    {formatCurrency(totales.debe)}
                  </span>
                </span>
                <span className="text-muted-foreground">
                  Total Haber:{" "}
                  <span className="font-mono tabular-nums font-medium text-foreground">
                    {formatCurrency(totales.haber)}
                  </span>
                </span>
                <span className="text-muted-foreground">
                  Diferencia:{" "}
                  <span
                    className={cn(
                      "font-mono tabular-nums font-medium",
                      cuadra ? "text-foreground" : "text-destructive"
                    )}
                  >
                    {formatCurrency(totales.diferencia)}
                  </span>
                </span>
              </div>
              <Badge
                variant="outline"
                className={cn(
                  "text-xs gap-1",
                  cuadra
                    ? "text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800"
                    : "text-destructive border-destructive/40"
                )}
                data-testid="evento-cuadre"
              >
                {cuadra ? "Cuadra ✓" : "No cuadra ✗"}
              </Badge>
            </div>
            {!cuadra && hayImporte && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Ajusta los importes: la suma del debe debe igualar la del haber (partida doble).
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            onClick={handleGuardar}
            disabled={!valido || guardando}
            data-testid="evento-guardar"
          >
            {guardando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {evento ? "Guardar cambios" : "Crear movimiento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card de un renting
// ---------------------------------------------------------------------------

function RentingCard({ renting, readOnly, onEditar, onBorrar }) {
  const p = renting.params || {};
  const fin =
    p.anio_inicio != null && p.duracion_anios != null
      ? p.anio_inicio + p.duracion_anios - 1
      : null;

  return (
    <Card data-testid={`renting-card-${renting.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Car className="h-4 w-4 text-primary shrink-0" />
              <h3 className="font-semibold truncate">{renting.nombre}</h3>
              <Badge variant="secondary" className="text-[11px] shrink-0">
                Renting
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="secondary" className="font-mono text-xs">
                {formatCurrency(p.cuota_anual)}/año
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {p.duracion_anios} año{p.duracion_anios !== 1 ? "s" : ""}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {p.anio_inicio}
                {fin != null && fin !== p.anio_inicio ? `–${fin}` : ""}
              </Badge>
            </div>
          </div>
          {!readOnly && (
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEditar(renting)}
                data-testid={`renting-editar-${renting.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onBorrar(renting)}
                data-testid={`renting-borrar-${renting.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge
            variant="outline"
            className="text-[11px] gap-1 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800"
          >
            <TrendingDown className="h-3 w-3" />
            → PyG: gasto explotación (baja EBITDA)
          </Badge>
        </div>
      </CardHeader>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Card de una póliza de crédito
// ---------------------------------------------------------------------------

function PolizaCard({ poliza, aniosProyectados, readOnly, onEditar, onBorrar }) {
  const [abierto, setAbierto] = useState(false);
  const p = poliza.params || {};
  const dispuesto = p.dispuesto_por_anio || {};
  const filas = (aniosProyectados || [])
    .map((a) => ({ anio: a, saldo: Number(dispuesto[String(a)] ?? 0) }))
    .filter((f) => Object.prototype.hasOwnProperty.call(dispuesto, String(f.anio)));

  return (
    <Card data-testid={`poliza-card-${poliza.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary shrink-0" />
              <h3 className="font-semibold truncate">{poliza.nombre}</h3>
              <Badge variant="secondary" className="text-[11px] shrink-0">
                Circulante
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="secondary" className="font-mono text-xs">
                límite {formatCurrency(p.limite)}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {nfPct.format(p.tipo_interes_pct ?? 0)}%
              </Badge>
            </div>
          </div>
          {!readOnly && (
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEditar(poliza)}
                data-testid={`poliza-editar-${poliza.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onBorrar(poliza)}
                data-testid={`poliza-borrar-${poliza.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge
            variant="outline"
            className="text-[11px] gap-1 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800"
          >
            <TrendingDown className="h-3 w-3" />
            → PyG: intereses sobre lo dispuesto
          </Badge>
          <Badge
            variant="outline"
            className="text-[11px] gap-1 text-sky-700 dark:text-sky-400 border-sky-300 dark:border-sky-800"
          >
            <Scale className="h-3 w-3" />
            → Balance: deuda CP (revolving)
          </Badge>
        </div>
      </CardHeader>
      {filas.length > 0 && (
        <CardContent className="pt-0">
          <Collapsible open={abierto} onOpenChange={setAbierto}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="px-0 text-xs text-muted-foreground hover:bg-transparent"
              >
                <ChevronDown
                  className={cn("h-3.5 w-3.5 mr-1 transition-transform", abierto && "rotate-180")}
                />
                Saldo dispuesto por año
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-2 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="h-8 text-xs w-[90px]">Año</TableHead>
                      <TableHead className="h-8 text-xs text-right">Dispuesto</TableHead>
                      <TableHead className="h-8 text-xs text-right">Interés estim.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filas.map((f) => (
                      <TableRow key={f.anio} className="hover:bg-muted/20">
                        <TableCell className="py-1.5 text-xs font-medium bg-primary/5">
                          {f.anio}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums bg-primary/5">
                          {formatCurrency(f.saldo)}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums text-muted-foreground bg-primary/5">
                          {formatCurrency((f.saldo * (p.tipo_interes_pct ?? 0)) / 100)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Card de un movimiento de balance (evento)
// ---------------------------------------------------------------------------

export function EventoCard({ evento, readOnly, onEditar, onBorrar }) {
  const [abierto, setAbierto] = useState(false);
  const p = evento.params || {};
  const apuntes = Array.isArray(p.apuntes) ? p.apuntes : [];
  const total = apuntes.reduce((acc, a) => acc + (Number(a.debe) || 0), 0);

  return (
    <Card data-testid={`evento-card-${evento.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ArrowLeftRight className="h-4 w-4 text-primary shrink-0" />
              <h3 className="font-semibold truncate">{evento.nombre}</h3>
              <Badge variant="secondary" className="text-[11px] shrink-0">
                Evento
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="secondary" className="text-xs">
                {p.anio}
              </Badge>
              <Badge variant="secondary" className="font-mono text-xs">
                {formatCurrency(total)}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {apuntes.length} apunte{apuntes.length !== 1 ? "s" : ""}
              </Badge>
            </div>
          </div>
          {!readOnly && (
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEditar(evento)}
                data-testid={`evento-editar-${evento.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onBorrar(evento)}
                data-testid={`evento-borrar-${evento.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge
            variant="outline"
            className="text-[11px] gap-1 text-sky-700 dark:text-sky-400 border-sky-300 dark:border-sky-800"
          >
            <Scale className="h-3 w-3" />
            → Balance: movimiento manual en {p.anio}
          </Badge>
        </div>
      </CardHeader>
      {apuntes.length > 0 && (
        <CardContent className="pt-0">
          <Collapsible open={abierto} onOpenChange={setAbierto}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="px-0 text-xs text-muted-foreground hover:bg-transparent"
              >
                <ChevronDown
                  className={cn("h-3.5 w-3.5 mr-1 transition-transform", abierto && "rotate-180")}
                />
                Detalle de los apuntes
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-2 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="h-8 text-xs">Concepto</TableHead>
                      <TableHead className="h-8 text-xs text-right">Debe</TableHead>
                      <TableHead className="h-8 text-xs text-right">Haber</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apuntes.map((a, idx) => (
                      <TableRow key={idx} className="hover:bg-muted/20">
                        <TableCell className="py-1.5 text-xs font-medium">
                          {DESTINO_LABEL[a.destino] || a.destino}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums">
                          {Number(a.debe) > 0 ? formatCurrency(a.debe) : "—"}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums">
                          {Number(a.haber) > 0 ? formatCurrency(a.haber) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 font-semibold bg-primary/5">
                      <TableCell className="py-1.5 text-xs">TOTAL</TableCell>
                      <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums">
                        {formatCurrency(total)}
                      </TableCell>
                      <TableCell className="py-1.5 text-xs text-right font-mono tabular-nums">
                        {formatCurrency(total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function FinanciacionContent({ empresaId, ejercicios, aniosProyectar, readOnly }) {
  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tipoNuevo, setTipoNuevo] = useState("prestamo"); // variante al crear
  const [prestamoEditando, setPrestamoEditando] = useState(null);
  const [prestamoBorrando, setPrestamoBorrando] = useState(null);
  // Renting y póliza (comparten el genérico crearProyeccionOperacion)
  const [rentingDialogOpen, setRentingDialogOpen] = useState(false);
  const [rentingEditando, setRentingEditando] = useState(null);
  const [polizaDialogOpen, setPolizaDialogOpen] = useState(false);
  const [polizaEditando, setPolizaEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [resumen, setResumen] = useState(null); // resumen_financiacion de la proyección
  // Subapartado activo (maqueta 2026-08-19): deuda_nueva | pool | otros | resumen
  const [subseccion, setSubseccion] = useState("deuda_nueva");

  // Años proyectados derivados de los ejercicios con datos + horizonte global
  const aniosProyectados = useMemo(() => {
    const ultimoAnio =
      ejercicios && ejercicios.length > 0
        ? Math.max(...ejercicios.map((e) => e.anio))
        : new Date().getFullYear();
    const n = Math.max(1, aniosProyectar || 1);
    return Array.from({ length: n }, (_, i) => ultimoAnio + 1 + i);
  }, [ejercicios, aniosProyectar]);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const res = await getProyeccionOperaciones(empresaId);
      setOperaciones(res.data || []);
      // Resumen anual de financiación (intereses al PyG + deuda al balance).
      // Es secundario: si falla, no rompe la pestaña de operaciones.
      try {
        const rr = await getProyeccionResultado(empresaId);
        setResumen(rr.data?.resumen_financiacion || null);
      } catch (_e) {
        setResumen(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cargar las operaciones de financiación");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  const prestamosNuevos = useMemo(
    () => operaciones.filter((op) => op.tipo === "prestamo"),
    [operaciones]
  );
  // "Pool bancario" = la deuda arrastrada declarada a mano como préstamo
  // existente (decisión 2026-08-19: sin integración con el Pool Bancario externo).
  const prestamosExistentes = useMemo(
    () => operaciones.filter((op) => op.tipo === "prestamo_existente"),
    [operaciones]
  );
  // E10: filtro del Pool (todos / vigentes / cancelados).
  const [filtroPool, setFiltroPool] = useState("todos");

  const rentings = useMemo(
    () => operaciones.filter((op) => op.tipo === "renting"),
    [operaciones]
  );

  const polizas = useMemo(
    () => operaciones.filter((op) => op.tipo === "poliza"),
    [operaciones]
  );

  // Etiqueta legible del tipo de operación (usada en el diálogo de borrado).
  const LABEL_TIPO = {
    prestamo: "préstamo",
    prestamo_existente: "préstamo",
    renting: "renting",
    poliza: "producto de circulante",
    evento_balance: "movimiento de balance",
  };

  const handleNuevo = (tipo = "prestamo") => {
    if (tipo === "renting") {
      setRentingEditando(null);
      setRentingDialogOpen(true);
      return;
    }
    if (tipo === "poliza") {
      setPolizaEditando(null);
      setPolizaDialogOpen(true);
      return;
    }
    setTipoNuevo(tipo);
    setPrestamoEditando(null);
    setDialogOpen(true);
  };

  const handleEditar = (prestamo) => {
    setPrestamoEditando(prestamo);
    setDialogOpen(true);
  };

  const handleEditarRenting = (renting) => {
    setRentingEditando(renting);
    setRentingDialogOpen(true);
  };

  const handleEditarPoliza = (poliza) => {
    setPolizaEditando(poliza);
    setPolizaDialogOpen(true);
  };

  // Guardado genérico (crear/actualizar) para cualquier tipo de operación.
  // Devuelve true si tuvo éxito, para que quien llama cierre su diálogo.
  const guardarOperacion = async (datos, editando, label) => {
    try {
      setGuardando(true);
      if (editando) {
        await actualizarProyeccionOperacion(empresaId, editando.id, {
          nombre: datos.nombre,
          params: datos.params,
        });
        toast.success(`${label} actualizado`);
      } else {
        await crearProyeccionOperacion(empresaId, datos);
        toast.success(`${label} creado`);
      }
      await cargar();
      return true;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Error al guardar el ${label.toLowerCase()}`
      );
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardar = async (datos) => {
    const ok = await guardarOperacion(datos, prestamoEditando, "Préstamo");
    if (ok) setDialogOpen(false);
  };

  const handleGuardarRenting = async (datos) => {
    const ok = await guardarOperacion(datos, rentingEditando, "Renting");
    if (ok) setRentingDialogOpen(false);
  };

  const handleGuardarPoliza = async (datos) => {
    const ok = await guardarOperacion(datos, polizaEditando, "Póliza");
    if (ok) setPolizaDialogOpen(false);
  };

  const handleBorrar = async () => {
    if (!prestamoBorrando) return;
    const label = LABEL_TIPO[prestamoBorrando.tipo] || "operación";
    try {
      setBorrando(true);
      await eliminarProyeccionOperacion(empresaId, prestamoBorrando.id);
      toast.success(`${label.charAt(0).toUpperCase()}${label.slice(1)} eliminado`);
      setPrestamoBorrando(null);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Error al eliminar el ${label}`);
    } finally {
      setBorrando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="financiacion-content">
      {/* Cabecera. W20.6-10: sin menú global "Nueva operación" — cada alta vive
          en su subapartado (préstamo nuevo y circulante en Deuda nueva, el
          existente en Pool) y los movimientos de balance en la pestaña
          "Otras hipótesis" del menú superior. */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Préstamos y financiación planificada: los intereses van al PyG y la deuda al
          balance con su cuadro de amortización.
        </p>
        {/* D2 (Héctor 29-09): el alta de circulante, arriba y a la vista (estaba
            escondida al final de Deuda nueva). El atajo «Nueva inversión CAPEX»
            (D1) se quitó en nov-2026 a petición de Pau: no ligaba la inversión
            con ninguna financiación y mezclaba dos pestañas; las inversiones se
            dan de alta solo desde CAPEX. */}
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                setSubseccion("deuda_nueva");
                handleNuevo("poliza");
              }}
              data-testid="fin-atajo-circulante"
              title="Póliza / factoring / confirming: deuda a corto plazo"
            >
              <CreditCard className="h-4 w-4" />
              Añadir préstamo c/p
            </Button>
          </div>
        )}
      </div>

      {/* Sub-navegación en 4 subapartados (maqueta 2026-08-19): la misma
          información de siempre, separada por concepto para no mezclar deuda
          nueva, deuda arrastrada, resto del grupo 66 y el consolidado. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { id: "deuda_nueva", icon: FileText, titulo: "Deuda nueva", sub: "préstamos nuevos a proyectar" },
          { id: "pool", icon: Landmark, titulo: "Pool bancario", sub: "deuda arrastrada de años anteriores" },
          { id: "otros", icon: Percent, titulo: "Otros financieros", sub: "costes 66x e ingresos 76x" },
          { id: "resumen", icon: Globe, titulo: "Resumen total", sub: "consolidado final de gastos financieros" },
        ].map(({ id, icon: Icon, titulo, sub }) => (
          <button
            key={id}
            type="button"
            onClick={() => setSubseccion(id)}
            data-testid={`fin-subseccion-${id}`}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted/30",
              subseccion === id ? "border-primary ring-1 ring-primary bg-primary/5" : "bg-card"
            )}
          >
            <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", subseccion === id ? "text-primary" : "text-muted-foreground")} />
            <span className="min-w-0">
              <span className="block font-semibold text-sm">{titulo}</span>
              <span className="block text-xs text-muted-foreground truncate">{sub}</span>
            </span>
          </button>
        ))}
      </div>

      {/* ── 1. DEUDA NUEVA: solo préstamos nuevos (+ resto de operaciones) ── */}
      {subseccion === "deuda_nueva" && (
        <div className="space-y-4">
          {prestamosNuevos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-muted/20">
              <div className="p-4 rounded-full bg-muted mb-4">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold mb-1">Sin préstamos nuevos planificados</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Un préstamo nuevo entra como caja el año de disposición; sus intereses van al
                PyG y su deuda pendiente al balance con el cuadro de amortización.
              </p>
              {!readOnly && (
                <Button variant="outline" className="mt-4" onClick={() => handleNuevo("prestamo")}>
                  <Plus className="h-4 w-4 mr-1" />
                  Añadir préstamo nuevo
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {prestamosNuevos.map((prestamo) => (
                <PrestamoCard
                  key={prestamo.id}
                  prestamo={prestamo}
                  aniosProyectados={aniosProyectados}
                  readOnly={readOnly}
                  onEditar={handleEditar}
                  onBorrar={setPrestamoBorrando}
                />
              ))}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleNuevo("prestamo")}
                  data-testid="anadir-otro-prestamo"
                  className="w-full border border-dashed rounded-lg py-3 text-sm text-muted-foreground hover:bg-muted/30 hover:text-foreground transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Añadir otro préstamo nuevo
                </button>
              )}
            </div>
          )}

          {/* Productos de circulante (W20.9): póliza / factoring / confirming.
              El caso "no me renuevan la póliza dispuesta" ya lo soporta el
              modelo: dispuesto a 0 un año genera la devolución (salida de caja). */}
          <div className="space-y-3">
            {polizas.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5">
                  <CreditCard className="h-4 w-4" />
                  Productos de circulante
                </h3>
                {polizas.map((poliza) => (
                  <PolizaCard
                    key={poliza.id}
                    poliza={poliza}
                    aniosProyectados={aniosProyectados}
                    readOnly={readOnly}
                    onEditar={handleEditarPoliza}
                    onBorrar={setPrestamoBorrando}
                  />
                ))}
              </>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => handleNuevo("poliza")}
                data-testid="anadir-circulante"
                className="w-full border border-dashed rounded-lg py-3 text-sm text-muted-foreground hover:bg-muted/30 hover:text-foreground transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Añadir producto de circulante (póliza / factoring / confirming)
              </button>
            )}
          </div>

          {/* Rentings ya creados: siguen visibles y editables. Las altas nuevas
              se hacen como driver de alquileres (grupo 62) en PyG-Gastos
              (decisión del cliente, W20.8). */}
          {rentings.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5">
                <Car className="h-4 w-4" />
                Rentings
              </h3>
              {rentings.map((renting) => (
                <RentingCard
                  key={renting.id}
                  renting={renting}
                  readOnly={readOnly}
                  onEditar={handleEditarRenting}
                  onBorrar={setPrestamoBorrando}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 2. POOL BANCARIO: deuda arrastrada (préstamos existentes) ── */}
      {subseccion === "pool" && (
        <div className="space-y-4">
          {prestamosExistentes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-muted/20">
              <div className="p-4 rounded-full bg-muted mb-4">
                <Landmark className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="font-semibold mb-1">Sin deuda arrastrada declarada</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Declara aquí la deuda que ya estaba dispuesta antes del primer año
                proyectado (saldo vivo, años restantes y tipo). Sus intereses (cuenta 662)
                van al PyG y la devolución de capital a caja y balance — sin entrada de
                caja, porque el dinero ya se recibió.
              </p>
              {!readOnly && (
                <Button variant="outline" className="mt-4" onClick={() => handleNuevo("prestamo_existente")}>
                  <Plus className="h-4 w-4 mr-1" />
                  Añadir préstamo existente
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Tabla agregada Año / Intereses / Capital / Total (maqueta) */}
              {resumen && resumen.filas && resumen.filas.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      <Landmark className="h-4 w-4 text-primary" />
                      Pool bancario
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Deuda arrastrada de años anteriores (préstamos existentes declarados).
                      Intereses = cuenta 662; Capital = amortización de principal.
                    </p>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    {/* W20.1: mismas cifras con el formato de la app de Pool
                        Bancario del cliente: Año | Total Cuotas | Amortización
                        | Intereses. Total Cuotas = amortización + intereses. */}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead>Año</TableHead>
                          <TableHead className="text-right">Total Cuotas</TableHead>
                          <TableHead className="text-right">Amortización</TableHead>
                          <TableHead className="text-right">Intereses</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resumen.filas.map((f) => (
                          <TableRow key={f.anio} className="hover:bg-muted/20">
                            <TableCell className="font-medium">{f.anio}</TableCell>
                            <TableCell className="text-right font-mono tabular-nums text-sm font-semibold">
                              {formatCurrency(
                                (f.intereses_prestamo_existente || 0) +
                                  (f.principal_devuelto_existentes || 0)
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums text-sm">
                              {formatCurrency(f.principal_devuelto_existentes)}
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums text-sm">
                              {formatCurrency(f.intereses_prestamo_existente)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-t-2 bg-muted/40 font-semibold">
                          <TableCell>TOTAL</TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-sm">
                            {formatCurrency(
                              resumen.filas.reduce(
                                (t, f) =>
                                  t +
                                  (f.intereses_prestamo_existente || 0) +
                                  (f.principal_devuelto_existentes || 0),
                                0
                              )
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-sm">
                            {formatCurrency(
                              resumen.filas.reduce((t, f) => t + (f.principal_devuelto_existentes || 0), 0)
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums text-sm">
                            {formatCurrency(
                              resumen.filas.reduce((t, f) => t + (f.intereses_prestamo_existente || 0), 0)
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
              {/* E10: filtro vigentes/cancelados + total cancelado, con traza. */}
              {prestamosExistentes.some((pr) => pr.params?.cancelacion) && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {["todos", "vigentes", "cancelados"].map((f) => (
                    <button key={f} type="button" onClick={() => setFiltroPool(f)}
                      className={cn("rounded-full border px-2.5 py-0.5 font-medium capitalize transition-colors",
                        filtroPool === f ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:text-foreground")}
                      data-testid={`pool-filtro-${f}`}>
                      {f}
                    </button>
                  ))}
                  <span className="ml-auto text-muted-foreground">
                    Cancelado:{" "}
                    <b className="font-mono tabular-nums">
                      {formatCurrency(prestamosExistentes.reduce((s, pr) => {
                        // P16 (Pau oct-2026): UNA fuente — `cancelado_real` del
                        // backend (saldo vivo EN la fecha según el cuadro). Sin
                        // fallback al saldo a 1 de enero: si no hay dato, suma 0
                        // (jamás una cifra inventada).
                        return s + (pr.cancelado_real
                          ?? (pr.cuadro || []).reduce((t, f) => t + (f.cancelado || 0), 0));
                      }, 0))}
                    </b>
                  </span>
                </div>
              )}
              <div className="space-y-3">
                {prestamosExistentes
                  // P16c: "vigentes" = con saldo vivo tras la cancelación — un
                  // préstamo cancelado PARCIALMENTE sigue vigente (antes
                  // desaparecía del filtro); "cancelados" = cualquier préstamo
                  // con cancelación declarada (total o parcial).
                  .filter((pr) => {
                    if (filtroPool === "todos") return true;
                    const c = pr.params?.cancelacion;
                    if (filtroPool === "cancelados") return !!c;
                    return !c || c.importe != null; // vigentes: sin cancelar o parcial
                  })
                  .map((prestamo) => (
                  <PrestamoCard
                    key={prestamo.id}
                    prestamo={prestamo}
                    aniosProyectados={aniosProyectados}
                    readOnly={readOnly}
                    onEditar={handleEditar}
                    onBorrar={setPrestamoBorrando}
                  />
                ))}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => handleNuevo("prestamo_existente")}
                    data-testid="anadir-otro-existente"
                    className="w-full border border-dashed rounded-lg py-3 text-sm text-muted-foreground hover:bg-muted/30 hover:text-foreground transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Añadir otro préstamo existente
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── 3. OTROS COSTES FINANCIEROS: grupo 66 (la 662 bloqueada) ── */}
      {subseccion === "otros" && (
        <div className="space-y-4">
          <TablaDrivers
            empresaId={empresaId}
            ejercicios={ejercicios}
            aniosProyectar={aniosProyectar}
            seccionPyg="gastos"
            filtroCodigos="66"
            storageKey={`finreport:nivel:${empresaId}:financiacion-otros`}
            nivelesPermitidos={["3D", "MAX"]}
            cuentasBloqueadas={BLOQUEO_662}
            textoBloqueo="Se calcula con los préstamos declarados en Pool bancario"
            cuentasInformativas={CUENTAS_GRUPO_66}
            tiposDriverPermitidos={["valor_fijo"]}
            onDriverSaved={cargar}
            readOnly={readOnly}
            titulo={null}
            descripcion={null}
          />
          {resumen && resumen.filas && resumen.filas.length > 0 && (
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 shrink-0">
                    <Percent className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-semibold">Resumen anual de otros costes</p>
                      <p className="text-xs text-muted-foreground">
                        Total de otros costes financieros del grupo 66 (excepto 662).
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 grid grid-flow-col auto-cols-fr gap-2 text-center overflow-x-auto">
                    {resumen.filas.map((f) => (
                      <div key={f.anio} className="rounded-md bg-primary/5 px-2 py-1.5">
                        <div className="text-xs text-primary font-semibold">{f.anio}</div>
                        <div className="font-mono tabular-nums text-sm">
                          {formatCurrency(f.intereses_otros)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Punto 5 de Héctor (oct-2026): INGRESOS financieros (grupo 76)
              driveables a importe fijo. Regla conservadora del motor: sin
              driver siguen a 0 en la proyección (comportamiento de siempre);
              con driver entran en el resultado financiero. */}
          <TablaDrivers
            empresaId={empresaId}
            ejercicios={ejercicios}
            aniosProyectar={aniosProyectar}
            seccionPyg="otros_ingresos"
            filtroCodigos="76"
            storageKey={`finreport:nivel:${empresaId}:financiacion-ifin`}
            nivelesPermitidos={["3D", "MAX"]}
            tiposDriverPermitidos={["valor_fijo"]}
            onDriverSaved={cargar}
            readOnly={readOnly}
            titulo="Ingresos financieros (grupo 76)"
            descripcion={"Intereses de depósitos, dividendos de participadas y otros ingresos financieros recurrentes. SIN driver van a 0 en la proyección (como siempre); define un importe fijo por año para que entren en el resultado financiero proyectado."}
          />
        </div>
      )}

      {/* ── 4. RESUMEN TOTAL: consolidado automático de los tres anteriores ── */}
      {subseccion === "resumen" &&
        (resumen && resumen.filas && resumen.filas.length > 0 ? (
          <div className="space-y-4">
            {/* A. Costes financieros → PyG */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-primary" />
                  Costes financieros → PyG
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Solo costes que impactan en la cuenta de resultados.
                </p>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table className={TABLA_ANIOS}>
                  <ColsAnios n={resumen.filas.length} anio={COL_ANIO_MONO} />
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Concepto</TableHead>
                      {resumen.filas.map((f) => (
                        <TableHead key={f.anio} className="text-right text-primary font-semibold">
                          {f.anio}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { clave: "intereses_prestamos_nuevos", label: "Intereses deuda nueva" },
                      { clave: "intereses_prestamo_existente", label: "Intereses Pool bancario — 662" },
                      { clave: "intereses_otros", label: "Otros costes financieros — grupo 66 (exc. 662)" },
                      { clave: "ingresos_financieros", label: "(−) Ingresos financieros — grupo 76 (si tienen driver)" },
                      { clave: "intereses_total", label: "TOTAL COSTES FINANCIEROS → PyG", destacada: true },
                    ].map(({ clave, label, destacada }) => (
                      <TableRow key={clave} className={destacada ? "border-t-2 font-semibold bg-primary/5" : "hover:bg-muted/20"}>
                        <TableCell className="font-medium">{label}</TableCell>
                        {resumen.filas.map((f) => (
                          <TableCell key={f.anio} className="text-right font-mono tabular-nums text-sm">
                            {formatCurrency(f[clave])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="px-4 py-2 text-[11px] text-muted-foreground">
                  El total alimenta el resultado financiero del PyG proyectado (coincide con
                  la pestaña Resultados).
                </p>
              </CardContent>
            </Card>

            {/* B. Impacto financiero / caja */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-primary" />
                  Impacto financiero / caja
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Movimientos de caja y servicio de la deuda.
                </p>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table className={TABLA_ANIOS}>
                  <ColsAnios n={resumen.filas.length} anio={COL_ANIO_MONO} />
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Concepto</TableHead>
                      {resumen.filas.map((f) => (
                        <TableHead key={f.anio} className="text-right text-primary font-semibold">
                          {f.anio}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { clave: "disposiciones", label: "Nueva financiación recibida" },
                      { clave: "principal_devuelto_nuevos", label: "Devolución capital deuda nueva" },
                      { clave: "principal_devuelto_existentes", label: "Devolución capital Pool bancario" },
                    ].map(({ clave, label }) => (
                      <TableRow key={clave} className="hover:bg-muted/20">
                        <TableCell className="font-medium">{label}</TableCell>
                        {resumen.filas.map((f) => (
                          <TableCell key={f.anio} className="text-right font-mono tabular-nums text-sm">
                            {formatCurrency(f[clave])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 font-semibold bg-primary/5">
                      <TableCell>TOTAL SERVICIO DE LA DEUDA</TableCell>
                      {resumen.filas.map((f) => (
                        <TableCell key={f.anio} className="text-right font-mono tabular-nums text-sm">
                          {formatCurrency((f.intereses_total || 0) + (f.principal_devuelto || 0))}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
                <p className="px-4 py-2 text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  El capital afecta a caja y balance, no a PyG. Servicio de la deuda =
                  intereses totales + devolución de capital.
                </p>
              </CardContent>
            </Card>

            {/* C. Deuda resultante en balance (LP / CP / total) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-primary" />
                  Deuda en balance
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table className={TABLA_ANIOS}>
                  <ColsAnios n={resumen.filas.length} anio={COL_ANIO_MONO} />
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Concepto</TableHead>
                      {resumen.filas.map((f) => (
                        <TableHead key={f.anio} className="text-right text-primary font-semibold">
                          {f.anio}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* W20.5: la deuda total se desglosa por ORIGEN (sublíneas):
                        préstamos antiguos (pool) / nuevos / resto histórica.
                        Invariante del backend: las 3 sublíneas suman el total. */}
                    {[
                      { clave: "deuda_lp", label: "Deuda a largo plazo" },
                      { clave: "deuda_cp", label: "Deuda a corto plazo" },
                      { clave: "deuda_existentes", label: "└ Préstamos antiguos (Pool bancario)", sub: true },
                      { clave: "deuda_nuevos", label: "└ Préstamos nuevos y pólizas", sub: true },
                      { clave: "deuda_resto", label: "└ Resto deuda histórica no declarada", sub: true },
                      { clave: "deuda_total", label: "Deuda total → Balance", destacada: true },
                    ].map(({ clave, label, destacada, sub }) => (
                      <TableRow
                        key={clave}
                        className={destacada ? "border-t-2 font-semibold bg-primary/5" : "hover:bg-muted/20"}
                      >
                        <TableCell className={cn("font-medium", sub && "pl-8 font-normal text-xs text-muted-foreground")}>
                          {label}
                        </TableCell>
                        {resumen.filas.map((f) => (
                          <TableCell
                            key={f.anio}
                            className={cn(
                              "text-right font-mono tabular-nums text-sm",
                              sub && "text-xs text-muted-foreground"
                            )}
                          >
                            {formatCurrency(f[clave])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="px-4 py-2 text-[11px] text-muted-foreground">
                  La deuda total coincide con la del balance proyectado y las tres
                  sublíneas (antiguos / nuevos / resto) suman exactamente el total.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground border rounded-lg bg-muted/20">
            <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              El resumen se calcula con la proyección: importa al menos un ejercicio
              histórico para verlo.
            </p>
          </div>
        ))}

      {/* Dialog crear/editar préstamo */}
      <PrestamoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        prestamo={prestamoEditando}
        tipoOperacion={tipoNuevo}
        aniosProyectados={aniosProyectados}
        onGuardar={handleGuardar}
        guardando={guardando}
      />

      {/* Dialog crear/editar renting */}
      <RentingDialog
        open={rentingDialogOpen}
        onOpenChange={setRentingDialogOpen}
        renting={rentingEditando}
        aniosProyectados={aniosProyectados}
        onGuardar={handleGuardarRenting}
        guardando={guardando}
      />

      {/* Dialog crear/editar póliza */}
      <PolizaDialog
        open={polizaDialogOpen}
        onOpenChange={setPolizaDialogOpen}
        poliza={polizaEditando}
        aniosProyectados={aniosProyectados}
        onGuardar={handleGuardarPoliza}
        guardando={guardando}
      />

      {/* Confirmación de borrado */}
      <AlertDialog open={!!prestamoBorrando} onOpenChange={(o) => !o && setPrestamoBorrando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Eliminar {LABEL_TIPO[prestamoBorrando?.tipo] || "operación"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará «{prestamoBorrando?.nombre}» y su impacto en el PyG, el balance
              y la tesorería proyectados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={borrando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBorrar();
              }}
              disabled={borrando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {borrando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
